import { useState } from "react";

import EditMentionModal from "@/containers/EditMentionModal";
import { useEpisode } from "@/containers/Episode/context";
import ExternalLink from "@/containers/Episode/partials/Body/partials/ExternalLink";
import Actions from "@/containers/Episode/partials/Mention/Actions";
import RemoveMention from "@/containers/Episode/partials/Mention/RemoveMention";
import Similar from "@/containers/Episode/partials/Mention/Similar";
import Translate from "@/containers/Episode/partials/Translate";
import { useEpisodes } from "@/contexts/EpisodesContext";
import { useI18n } from "@/contexts/i18nContext";
import { Mention as MentionType } from "@/types/Mention";
import { cx } from "@/utils";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import Typography from "@components/Typography";
import UndoRemovedMention from "@components/UndoRemovedMention";

import styles from "./Mention.module.css";

const Mention: React.FunctionComponent<{
  mention: MentionType;
  hideActions?: boolean;
  hideMetadata?: boolean;
  hideExternalLink?: boolean;
  className?: string;
}> = ({
  className,
  hideExternalLink,
  mention,
  hideActions: forceHideActions,
}) => {
  const {
    hideActions,
    hideSimilar,
    mentions,
    removedMentionIds,
    channel_language,
  } = useEpisode();
  const { language } = useI18n();
  const { updateMention } = useEpisodes();
  const [editing, setEditing] = useState(false);
  const allMentionsRemoved = removedMentionIds.length === mentions.length;
  const isRemoved = removedMentionIds.includes(mention.public_id);

  const channelNotInUserLanguage = channel_language !== language;

  return (
    <>
      <div
        className={cx(styles.root, "mention", className)}
        data-mention-id={mention.public_id}
      >
        <div>
          <Typography
            data-testid="mention-text-content"
            className={cx(
              { [styles.lineThrough]: isRemoved },
              "highlight-match"
            )}
            component="span"
            dangerouslySetInnerHTML={{ __html: mention?.trimmed_content }}
          />
          {isRemoved && !allMentionsRemoved ? (
            <UndoRemovedMention currentId={mention.public_id} />
          ) : (
            <div className={styles.actions}>
              {channelNotInUserLanguage && !hideActions && (
                <Translate mentions={[mention]} toKey="trimmed_content" />
              )}
              {!hideExternalLink && <ExternalLink />}
              {!forceHideActions && !hideActions && (
                <AccessControl level={ACCESS_LEVELS.USER}>
                  <RemoveMention currentId={mention.public_id} />
                  <Actions mention={mention} setEditing={setEditing} />
                </AccessControl>
              )}
            </div>
          )}
          {!hideSimilar && mention.similar?.length > 0 && (
            <Similar
              mentions={mention.similar}
              episode_pk={mention.episode_pk}
            />
          )}
        </div>
      </div>
      <EditMentionModal
        show={editing}
        onHide={() => setEditing(false)}
        mention={mention}
        updateMention={updateMention}
      />
    </>
  );
};

export default Mention;
